import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ago } from "@/lib/sk";
import { Inspector } from "@/components/canvas/Inspector";

/** «Kvalitetssjekk» – the review lives on the case; a fresh run replaces it. Findings only, never a change. */
export function useAnalyse(caseId) {
  const [analysis, setAnalysis] = useState(null);
  const [state, setState] = useState("idle");
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    setAnalysis(null);
    api.get(`/projects/${caseId}/kalkyle/analyse`).then((r) => { if (alive.current) setAnalysis(r.data.analysis); }).catch(() => {});
  }, [caseId]);
  const run = useCallback(async () => {
    setState("running");
    try {
      const r = await api.post(`/projects/${caseId}/kalkyle/analyse`, {});
      if (alive.current) { setAnalysis(r.data.analysis); setState("done"); }
    } catch (e) {
      toast.error(errMsg(e));
      if (alive.current) setState("error");
    }
  }, [caseId]);
  return { analysis, state, run, running: state === "running" };
}

const KIND = { lav_margin: "Lav margin", venter_pris: "Venter på pris", mengde: "Mangler mengde", prisform: "Uten prisform", dobbel: "Mulig dobbeltelling", simulert: "Simulert kost", revisjon: "Endret siden siste versjon" };
const KIND_ORDER = ["lav_margin", "revisjon", "dobbel", "prisform", "venter_pris", "mengde", "simulert"];
const DOT = { hoy: "bg-[hsl(var(--destructive))]", middels: "bg-[hsl(var(--v-assumed))]", lav: "bg-black/20" };
const findingsOf = (analysis) => (analysis ? KIND_ORDER.map((k) => [k, analysis.findings.filter((f) => f.kind === k)]).filter(([, l]) => l.length) : []);
const countWord = (n) => `${n} ${n === 1 ? "funn" : "funn"}`;

function Finding({ f, onOpen }) {
  const id = f.item_ids?.[0];
  return (
    <li className="py-3" data-testid={`analyse-finding-${f.kind}-${(f.refs?.[0] || "x").replace(/[^a-zA-Z0-9.]/g, "")}`} data-severity={f.severity}>
      <div className="flex items-start gap-3">
        <span className={cn("mt-[7px] h-[7px] w-[7px] shrink-0 rounded-full", DOT[f.severity] || DOT.lav)} aria-hidden />
        <div className="min-w-0 flex-1">
          {id ? <button type="button" onClick={() => onOpen(id)} className="group/f text-left text-[15px] font-medium leading-[1.4] text-foreground hover:text-[hsl(var(--navy))]" data-testid="analyse-finding-open">{f.title} <ArrowRight className="inline h-3.5 w-3.5 -translate-y-px opacity-0 transition-opacity group-hover/f:opacity-70" /></button> : <div className="text-[15px] font-medium leading-[1.4] text-foreground">{f.title}</div>}
          <p className="mt-0.5 text-[14px] leading-[1.5] text-foreground/80">{f.detail}</p>
          <p className="k-meta mt-0.5">{f.basis}</p>
          {f.refs?.length > 1 ? (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {f.refs.slice(0, 12).map((r, i) => { const iid = f.item_ids?.[i]; return <button key={r} type="button" disabled={!iid} onClick={() => iid && onOpen(iid)} className="rounded-[6px] bg-black/[0.04] px-1.5 py-0.5 font-mono text-[12px] text-foreground/75 transition-colors hover:bg-black/[0.08] hover:text-foreground disabled:cursor-default disabled:hover:bg-black/[0.04]">{r}</button>; })}
              {f.refs.length > 12 ? <span className="text-[12px] text-muted-foreground">+{f.refs.length - 12}</span> : null}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function Group({ kind, list, onOpen }) {
  const [open, setOpen] = useState(list.length <= 4 || list.some((f) => f.severity === "hoy"));
  return (
    <section className="border-t border-black/[0.06] py-2 first:border-t-0" data-testid={`analyse-group-${kind}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-baseline gap-2.5 py-1.5 text-left" data-testid={`analyse-group-toggle-${kind}`}>
        <span className="text-[14px] font-semibold text-foreground">{KIND[kind] || kind}</span>
        <span className="k-meta tabular">{list.length}</span>
        <ChevronDown className={cn("ml-auto h-4 w-4 self-center text-muted-foreground/60 transition-transform duration-200", !open && "-rotate-90")} />
      </button>
      {open ? <ul className="divide-y divide-black/[0.05]">{list.map((f, i) => <Finding key={`${f.kind}-${i}`} f={f} onOpen={onOpen} />)}</ul> : null}
    </section>
  );
}

/**
 * The findings in the right-hand panel – beside the list, not under it. The severe first, the quiet ones folded; every
 * row opens its post in the same panel («‹ Kvalitetssjekk» brings the findings back). The narrative is the assistant's
 * words on top of verified findings. Nothing here changes a number.
 */
export function QualityPanel({ analyse, onOpen, onClose }) {
  const { analysis, running, state, run } = analyse;
  const groups = findingsOf(analysis);
  const sub = running ? "Går gjennom marginer, priser, mengder og endringer …" : analysis ? <>{countWord(analysis.findings.length)} · {ago(analysis.created_at)}{analysis.stale ? <> · <span className="text-[hsl(var(--v-assumed))]">kalkylen er endret siden</span></> : null}</> : null;
  const actions = analysis && !running ? <button type="button" onClick={run} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground" aria-label="Kjør kvalitetssjekken på nytt" title="Kjør på nytt" data-testid="analyse-rerun"><RefreshCw className="h-4 w-4" /></button> : null;
  return (
    <Inspector onClose={onClose} title="Kvalitetssjekk" sub={sub} actions={actions} testId="detail-tool-quality" label="Kvalitetssjekk" noWide>
      <div data-testid="kalkyle-analyse" data-state={running ? "running" : analysis?.stale ? "stale" : analysis ? "fresh" : "none"}>
        {running ? (
          <div className="space-y-3 pt-1" data-testid="analyse-running" aria-busy="true">
            {[0, 1, 2].map((i) => <div key={i} className="h-4 animate-pulse rounded bg-black/[0.05]" style={{ width: `${88 - i * 16}%` }} />)}
          </div>
        ) : null}
        {!running && state === "error" && !analysis ? <p className="text-[15px] text-muted-foreground">Kvalitetssjekken stoppet. <button type="button" onClick={run} className="font-medium text-[hsl(var(--navy))] hover:underline">Prøv igjen</button></p> : null}
        {analysis && !running ? (
          <>
            {analysis.narrative ? <p className="text-[15px] leading-[1.6] text-foreground" data-testid="analyse-narrative">{analysis.narrative}</p> : null}
            {!analysis.findings.length ? <p className="mt-3 text-[15px] text-muted-foreground" data-testid="analyse-empty">Ingen funn – ingen lav margin, ingen linjer som venter, ingen tegn til dobbeltelling.</p> : (
              <div className={cn(analysis.narrative && "mt-4")} data-testid="analyse-findings" data-count={analysis.findings.length}>{groups.map(([k, l]) => <Group key={k} kind={k} list={l} onOpen={onOpen} />)}</div>
            )}
            <p className="k-meta mt-5">Kvalitetssjekken endrer ingenting. Tall og poster er lest fra kalkylen slik den står nå.</p>
          </>
        ) : null}
      </div>
    </Inspector>
  );
}

/** One line in the Sammendrag: where the last check stands and the way into the panel – never the findings themselves. */
export function QualityLine({ analyse, onOpenPanel }) {
  const { analysis, running } = analyse;
  const n = analysis?.findings.length ?? 0;
  const severe = analysis ? analysis.findings.filter((f) => f.severity === "hoy").length : 0;
  return (
    <section className="mt-10" data-testid="kalkyle-quality" data-state={running ? "running" : analysis?.stale ? "stale" : analysis ? "fresh" : "none"}>
      <div className="flex items-baseline justify-between gap-3"><h3 className="k-label">Kvalitetssjekk</h3></div>
      <button type="button" onClick={onOpenPanel} className="group -mx-2 mt-1 flex w-full items-baseline gap-4 rounded-[8px] px-2 py-3 text-left outline-none transition-colors hover:bg-black/[0.02] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30" data-testid="kalkyle-quality-open">
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] text-foreground">
            {running ? "Sjekker kalkylen …" : !analysis ? "Ikke kjørt ennå" : n ? <>{countWord(n)}{severe ? <span className="text-[hsl(var(--destructive))]"> · {severe} {severe === 1 ? "alvorlig" : "alvorlige"}</span> : null}</> : "Ingen funn"}
          </span>
          <span className="k-meta block">{running ? "Marginer, priser, mengder, dobbeltføringer og endringer siden siste versjon" : !analysis ? "Marginer, priser, mengder, dobbeltføringer og endringer siden siste versjon" : <>{ago(analysis.created_at)}{analysis.stale ? " · kalkylen er endret siden" : ""}</>}</span>
        </span>
        <span className="k-meta shrink-0 opacity-0 transition-opacity group-hover:opacity-100">{analysis ? "Åpne" : "Kjør"} <ArrowRight className="inline h-3.5 w-3.5" /></span>
      </button>
    </section>
  );
}
